import { useCallback, useEffect, useMemo, useState } from 'react'
import { Cell, Column, SortingRule } from 'react-table'
import _isEmpty from 'lodash/isEmpty'
import IntlFormattedDate from 'app/i18n/IntlFormattedDate'
import { MobileSortBy, Table, TableLoader } from 'components/Table'
import { SoftwareVersionCell } from '../HomeSoftwareUpdates/components/SoftwareVersionCell'
import { prettyVersionNumber } from 'features/home/utils'
import {
  HomeCloudSoftwareHistoryType,
  CloudSoftwareHistoryEnum,
} from 'features/home/types'
import HomeCloudSoftwareHistoryMobile from './components/HomeCloudSoftwareHistoryMobile'
import { getSortOptions } from 'components/Table/utils'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import ShowMoreButton from 'components/Button/ShowMoreButton'
import SomethingWentWrongBlock from 'components/SomethingWentWrongBlock'
import { SOFTWARE_PROGRESS_HEADERS } from './const'
import { selectedHomeSelector } from 'features/home/selectors'
import { fetchCloudSoftwareHistoryThunk } from './slice'
import {
  allCloudSoftwareHistoryFetchedSelector,
  allCloudSoftwareHistoryNextSelector,
  cloudSoftwareHistorySelector,
} from './selectors'
import {
  HomeCloudSoftwareHistoryContainer,
  HomeSoftwareRowStyles,
  HomeSoftwareVersion,
} from './styles'
import {
  HomeSoftwareEmptyBlock,
  HomeSoftwareUpdateTableContainer,
} from '../styles'
import { usePullForHostUpdates } from '../utils'
import SoftwareUpdateStatus from './components/UpdateStatus'

const VersionCell = (cell: Cell<HomeCloudSoftwareHistoryType>) => {
  const version = prettyVersionNumber(cell.value)

  return cell.value ? (
    <SoftwareVersionCell version={version} />
  ) : (
    <HomeSoftwareVersion>-</HomeSoftwareVersion>
  )
}

const DateCell = (cell: Cell) => (
  <IntlFormattedDate date={cell?.value * 1000} withTime defaultReturn="-" />
)

const StatusCell = (cell: Cell<HomeCloudSoftwareHistoryType>) => {
  const update = cell.row.original

  return (
    <SoftwareUpdateStatus
      progress={update.progress}
      state={update.state}
      message={update.message}
    />
  )
}

function HomeCloudSoftwareHistory() {
  const dispatch = useAppDispatch()
  const selectedHome = useAppSelector(selectedHomeSelector.data)
  const cloudSoftwareHistory = useAppSelector(cloudSoftwareHistorySelector)
  const allCloudSoftwareHistoryFetched = useAppSelector(
    allCloudSoftwareHistoryFetchedSelector
  )
  const allCloudSoftwareHistoryNext = useAppSelector(
    allCloudSoftwareHistoryNextSelector
  )

  usePullForHostUpdates()

  const [isLoading, setIsLoading] = useState(false)
  const [showError, setShowError] = useState(false)
  const [nextPage, setNextPage] = useState<string | undefined>()

  useEffect(() => {
    if (selectedHome && !allCloudSoftwareHistoryFetched) {
      try {
        setIsLoading(true)
        dispatch(
          fetchCloudSoftwareHistoryThunk({
            homeId: selectedHome.id,
            partNumber: selectedHome.hostType,
            params: {
              next: nextPage,
            },
          })
        )
      } catch (e) {
        setShowError(true)
      } finally {
        setIsLoading(false)
      }
    }
  }, [dispatch, selectedHome, nextPage, allCloudSoftwareHistoryFetched])

  const handleOnShowMoreClick = useCallback(() => {
    setNextPage(allCloudSoftwareHistoryNext)
  }, [allCloudSoftwareHistoryNext])

  const headers = useMemo(
    () => ({
      versionNumber: {
        label: 'Version',
        id: SOFTWARE_PROGRESS_HEADERS.VERSION,
      },
      created: {
        label: 'Started',
        id: SOFTWARE_PROGRESS_HEADERS.STARTED,
      },
      updated: {
        label: 'Last Updated',
        id: SOFTWARE_PROGRESS_HEADERS.LAST_UPDATED,
      },
      state: {
        label: 'Status',
        id: SOFTWARE_PROGRESS_HEADERS.STATUS,
      },
    }),
    []
  )

  const columns: readonly Column<HomeCloudSoftwareHistoryType>[] = useMemo(
    () => [
      {
        id: headers.versionNumber.id,
        Header: headers.versionNumber.label,
        accessor: (row) => row.versionNumber,
        Cell: VersionCell,
        percentWidth: '20%',
      },
      {
        id: headers.created.id,
        Header: headers.created.label,
        accessor: (row) => row.created,
        Cell: DateCell,
        percentWidth: '20%',
      },
      {
        id: headers.updated.id,
        Header: headers.updated.label,
        accessor: (row) => row.updated,
        Cell: DateCell,
        percentWidth: '20%',
      },
      {
        id: headers.state.id,
        Header: headers.state.label,
        accessor: (row) => row.state,
        Cell: StatusCell,
        percentWidth: '30%',
      },
    ],
    [headers]
  )

  const sortOptions = useMemo(() => getSortOptions(headers, true), [headers])
  const defaultSortBy = sortOptions[1]
  const [sortBy, setSortBy] =
    useState<SortingRule<HomeCloudSoftwareHistoryType>>(defaultSortBy)

  const filteredCloudSoftwareHistory = cloudSoftwareHistory.filter(
    ({ requestedBy, state }) =>
      requestedBy !== 'Home' ||
      (state !== CloudSoftwareHistoryEnum.download &&
        state !== CloudSoftwareHistoryEnum.ready)
  )

  return (
    <HomeCloudSoftwareHistoryContainer>
      <MobileSortBy<HomeCloudSoftwareHistoryType>
        headers={headers}
        sortOptions={sortOptions}
        sortBy={sortBy}
        setSortBy={setSortBy}
      />

      {isLoading && <TableLoader />}

      {showError && <SomethingWentWrongBlock />}

      {!isLoading && !showError && (
        <>
          {_isEmpty(cloudSoftwareHistory) ? (
            <HomeSoftwareEmptyBlock>
              No history available
            </HomeSoftwareEmptyBlock>
          ) : (
            <HomeSoftwareUpdateTableContainer>
              <Table
                isPaginated={false}
                columns={columns}
                data={filteredCloudSoftwareHistory}
                defaultSortBy={defaultSortBy}
                sortBy={sortBy}
                setSortBy={setSortBy}
                mobileViewComponent={HomeCloudSoftwareHistoryMobile}
                rowStyles={HomeSoftwareRowStyles}
                fieldAsUniqueKey={'updateId'}
              />
            </HomeSoftwareUpdateTableContainer>
          )}
        </>
      )}
      {!allCloudSoftwareHistoryFetched && (
        <ShowMoreButton onClick={handleOnShowMoreClick} disabled={isLoading} />
      )}
    </HomeCloudSoftwareHistoryContainer>
  )
}

export default HomeCloudSoftwareHistory
